/**
 * RoundCardBase
 *
 * Outer shell shared by all per-game-type round cards in the admin dashboard.
 * Owns the consistent visual frame (border colour by status, disabled opacity,
 * header with round number + status badge) and exposes named slots for the
 * pieces that differ between game types.
 *
 * Slots (composition, not inheritance):
 *   - timer:    optional active-round timer component
 *   - children: round-body content (settings summary, players, etc.)
 *   - actions:  button row (wrap in RoundActionsBar for consistent styling)
 *   - footer:   optional trailing extras (Marco Polo events, scoring-failed banner)
 */

import { ReactNode } from 'react';
import { statusBadgeClass } from '@swtorbit/shared-components';

export interface RoundCardBaseProps {
  index: number;
  status: string;
  disabled?: boolean;
  borderColor: string;
  timer?: ReactNode;
  actions: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

export function RoundCardBase({
  index,
  status,
  disabled,
  borderColor,
  timer,
  actions,
  footer,
  children,
}: RoundCardBaseProps) {
  return (
    <div
      className={`bg-space bg-o-50 border-md rounded-base p-sm mb-xs d-flex flex-column gap-xs text-white${disabled ? ' o-50' : ''}`}
      style={{ borderColor }}
    >
      <div className="d-flex justify-content-between align-items-center gap-sm">
        <h4 className="fw-bold">Round {index + 1}</h4>
        <span className={statusBadgeClass(status)}>
          {status.toUpperCase()}
          {disabled && ' (DISABLED)'}
        </span>
      </div>

      {timer}

      <div className="d-flex flex-column gap-xs">{children}</div>

      {actions}

      {footer}
    </div>
  );
}
